import type { ChipProps } from '@carvana/ds-chip';
import Skeleton from '@carvana/ds-skeleton';
import cx from 'classnames';
import type { ReactNode } from 'react';
import Chip from '@/common/Chip';
import type { SuggestedFilter } from '~/contracts/common';
import toLowerKebabCase from '~/utilities/toLowerKebabCase';
import { suggestedFilterChipsSkeletonWidths } from './constants';
import splitIntoTwoRows from './splitIntoTwoRows';

const MAX_ITEMS_FOR_SINGLE_ROW = 4;

export type SuggestedFilterChipItem = {
  count: number;
  key: string;
  label: string;
  onClick: () => void;
};

export function mapSuggestedFiltersToChipItems(
  suggestedFilters: Array<SuggestedFilter>,
  onSelect: (suggestedFilter: SuggestedFilter) => void
): Array<SuggestedFilterChipItem> {
  return suggestedFilters.map((suggestedFilter, i) => ({
    count: suggestedFilter.totalMatchedInventory,
    key: `${suggestedFilter.displayName}-${i}`,
    label: suggestedFilter.displayName,
    onClick: () => onSelect(suggestedFilter)
  }));
}

type FilterChipProps = {
  item: SuggestedFilterChipItem;
  shape: ChipProps['shape'];
  showCounts: boolean;
};

function FilterChip({ item, shape, showCounts }: FilterChipProps) {
  return (
    <Chip
      className="items-baseline px-lg t-label-sm"
      data-adobe-id={toLowerKebabCase('suggested-filters', item.label)}
      data-analytics-id={null}
      data-testid="suggested-filter"
      onClick={item.onClick}
      shape={shape}
      testId="suggested-filter"
    >
      {item.label}
      {showCounts && (
        <span className="text-text-weak t-body-xs">
          ({item.count.toLocaleString()})
        </span>
      )}
    </Chip>
  );
}

type FilterChipSkeletonProps = {
  shape: ChipProps['shape'];
  width: number;
};

function FilterChipSkeleton({ shape, width }: FilterChipSkeletonProps) {
  return (
    <Skeleton
      className={cx('shrink-0 py-[7px] t-body-sm', {
        'rounded-full': shape === 'pill',
        'rounded-md': shape === 'rectangle'
      })}
      data-testid="suggested-filter-skeleton"
      height="34px"
      width={`${width}px`}
    >
      &nbsp;
    </Skeleton>
  );
}

type OwnProps = {
  isLoading: boolean;
  items: Array<SuggestedFilterChipItem>;
  layout?: 'row' | 'twoRow';
  leadingElement?: ReactNode;
  shape?: ChipProps['shape'];
  showCounts?: boolean;
};

export default function SuggestedFilterChips({
  isLoading,
  items,
  layout = 'row',
  leadingElement,
  shape = 'rectangle',
  showCounts = true
}: OwnProps) {
  const renderSkeleton = (width: number, key: number) => (
    <FilterChipSkeleton key={key} shape={shape} width={width} />
  );

  const renderChip = (item: SuggestedFilterChipItem) => (
    <FilterChip
      key={item.key}
      item={item}
      shape={shape}
      showCounts={showCounts}
    />
  );

  if (isLoading) {
    if (layout === 'twoRow') {
      const [topRowWidths, bottomRowWidths] = splitIntoTwoRows(
        suggestedFilterChipsSkeletonWidths
      );

      return (
        <div className="flex flex-col gap-md">
          <div className="flex gap-md">
            {leadingElement}
            {topRowWidths.map((width, index) => renderSkeleton(width, index))}
          </div>
          <div className="flex gap-md">
            {bottomRowWidths.map((width, index) =>
              renderSkeleton(width, topRowWidths.length + index)
            )}
          </div>
        </div>
      );
    }

    return (
      <>
        {suggestedFilterChipsSkeletonWidths.map((width, index) =>
          renderSkeleton(width, index)
        )}
      </>
    );
  }

  if (layout === 'twoRow') {
    if (items.length <= MAX_ITEMS_FOR_SINGLE_ROW) {
      return (
        <div className="flex gap-md">
          {leadingElement}
          {items.map(renderChip)}
        </div>
      );
    }

    const [topRow, bottomRow] = splitIntoTwoRows(items);

    return (
      <div className="flex flex-col gap-md">
        <div className="flex gap-md">
          {leadingElement}
          {topRow.map(renderChip)}
        </div>
        <div className="flex gap-md">{bottomRow.map(renderChip)}</div>
      </div>
    );
  }

  return <>{items.map(renderChip)}</>;
}
